import { useQuery, UseQueryResult } from '@tanstack/react-query';
import type { ScoredRoute } from '../components/RouteResults';
import type { SearchCriteria } from '../components/FlightSearch';

// API base URL - should be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.staffedskies.com';

interface RecommendationResponse {
  routes: ScoredRoute[];
  dataTimestamp: number;
  dataAge: number;
}

interface CoverageScoreRequest {
  departure: string;
  arrival: string;
  route?: string;
}

interface CoverageSnapshot {
  timestamp: number;
  dataAge: number;
  controllers: {
    callsign: string;
    type: string;
    facility: string;
  }[];
  coverageBubbles: {
    lat: number;
    lon: number;
    radiusNm: number;
    controllerType: 'DEL' | 'GND' | 'TWR' | 'APP' | 'CTR';
    callsign: string;
  }[];
  stats: {
    totalControllers: number;
    ctrCount: number;
    appCount: number;
    twrCount: number;
  };
}

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Generic fetch wrapper with error handling
async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `API request failed: ${response.statusText}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error occurred',
      undefined,
      error
    );
  }
}

// Hook for fetching flight recommendations
export function useRecommendations(
  criteria: SearchCriteria | null
): UseQueryResult<RecommendationResponse, ApiError> {
  return useQuery({
    queryKey: ['recommendations', criteria],
    queryFn: async () => {
      if (!criteria) {
        throw new ApiError('Search criteria is required');
      }

      return apiFetch<RecommendationResponse>('/recommendations', {
        method: 'POST',
        body: JSON.stringify(criteria),
      });
    },
    enabled: !!criteria,
    staleTime: 60000, // 60 seconds
    retry: 1,
  });
}

// Hook for fetching coverage score for a specific route
export function useCoverageScore(
  request: CoverageScoreRequest | null
): UseQueryResult<ScoredRoute, ApiError> {
  return useQuery({
    queryKey: ['coverageScore', request],
    queryFn: async () => {
      if (!request) {
        throw new ApiError('Route information is required');
      }

      return apiFetch<ScoredRoute>('/coverage-score', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },
    enabled: !!request,
    staleTime: 60000, // 60 seconds
    retry: 1,
  });
}

// Hook for fetching current coverage snapshot
export function useCoverageSnapshot(): UseQueryResult<CoverageSnapshot, ApiError> {
  return useQuery({
    queryKey: ['coverageSnapshot'],
    queryFn: () => apiFetch<CoverageSnapshot>('/coverage-snapshot'),
    staleTime: 60000, // 60 seconds
    refetchInterval: 120000, // Refetch every 2 minutes
    retry: 2,
  });
}

// Helper hook to get user-friendly error messages
export function useApiErrorMessage(error: ApiError | null): string {
  if (!error) return '';

  if (error.status === 400) {
    return 'Invalid search parameters. Please check your input and try again.';
  }

  if (error.status === 404) {
    return 'No routes found matching your criteria. Try adjusting your search.';
  }

  if (error.status === 500) {
    return 'Server error occurred. Please try again later.';
  }

  if (error.status === 503) {
    return 'Service temporarily unavailable. Please try again in a few moments.';
  }

  if (!error.status) {
    return 'Network error. Please check your connection and try again.';
  }

  return error.message || 'An unexpected error occurred. Please try again.';
}
